'use client'

import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { Undo2, X, CheckCircle, AlertTriangle, Clock } from 'lucide-react'

/**
 * BIRHAUS Undo System
 * 
 * Implements BIRHAUS principle #5: "Undo over confirm"
 * 
 * Core Philosophy:
 * - Actions execute immediately without confirmation dialogs
 * - Users can undo critical actions within a time window
 * - Provides clear feedback about what was done and how to undo
 * - Reduces cognitive load by eliminating confirmation interruptions
 */

// BIRHAUS: Utility function (replaces @/lib/utils cn)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface UndoAction {
  id: string
  type: 'create' | 'update' | 'delete' | 'transfer' | 'approve'
  entityType: string
  entityId: string
  description: string
  undoFunction: () => Promise<void>
  createdAt: Date
  expiresAt: Date
  priority: 'low' | 'normal' | 'high' | 'critical'
  icon?: ReactNode
  metadata?: Record<string, unknown>
}

interface UndoContextType {
  actions: UndoAction[]
  addAction: (action: Omit<UndoAction, 'id' | 'createdAt' | 'expiresAt'>) => string
  undoAction: (actionId: string) => Promise<void>
  dismissAction: (actionId: string) => void
  clearExpired: () => void
  getActionsByType: (type: string) => UndoAction[]
}

const UndoContext = createContext<UndoContextType | null>(null)

export function useUndo() {
  const context = useContext(UndoContext)
  if (!context) {
    throw new Error('useUndo must be used within an UndoProvider')
  }
  return context
}

// BIRHAUS: Undo Provider with automatic cleanup
export interface UndoProviderProps {
  children: ReactNode
  defaultTimeoutMs?: number
  maxActions?: number
}

export function UndoProvider({ 
  children, 
  defaultTimeoutMs = 10000, // 10 seconds default
  maxActions = 5 
}: UndoProviderProps) {
  const [actions, setActions] = useState<UndoAction[]>([])
  
  // Cleanup expired actions
  useEffect(() => {
    const interval = setInterval(() => {
      setActions(prev => prev.filter(action => action.expiresAt > new Date()))
    }, 1000)
    
    return () => clearInterval(interval)
  }, [])
  
  const addAction = (actionData: Omit<UndoAction, 'id' | 'createdAt' | 'expiresAt'>): string => {
    const id = `undo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const createdAt = new Date()
    const expiresAt = new Date(createdAt.getTime() + defaultTimeoutMs)
    
    const newAction: UndoAction = {
      ...actionData,
      id,
      createdAt,
      expiresAt
    }
    
    setActions(prev => {
      // Remove oldest actions if we exceed maxActions
      const updated = [newAction, ...prev].slice(0, maxActions)
      return updated
    })
    
    return id
  }
  
  const undoAction = async (actionId: string): Promise<void> => {
    const action = actions.find(a => a.id === actionId)
    if (!action) {
      throw new Error('Action not found or expired')
    }
    
    try {
      await action.undoFunction()
      // Remove the action after successful undo
      setActions(prev => prev.filter(a => a.id !== actionId))
    } catch (error) {
      console.error('Failed to undo action:', error)
      throw error
    }
  }
  
  const dismissAction = (actionId: string) => {
    setActions(prev => prev.filter(a => a.id !== actionId))
  }
  
  const clearExpired = () => {
    setActions(prev => prev.filter(action => action.expiresAt > new Date()))
  }
  
  const getActionsByType = (type: string) => {
    return actions.filter(action => action.entityType === type)
  }
  
  return (
    <UndoContext.Provider value={{
      actions,
      addAction,
      undoAction,
      dismissAction,
      clearExpired,
      getActionsByType
    }}>
      {children}
    </UndoContext.Provider>
  )
}

// BIRHAUS: Simple Button component for the library
interface BirhausButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'quiet'
  size?: 'sm' | 'md'
}

function BirhausButton({ 
  className, 
  variant = 'secondary', 
  size = 'md', 
  children, 
  ...props 
}: BirhausButtonProps) {
  const baseStyles = "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
  
  const variants = {
    primary: "bg-primary-600 text-white hover:bg-primary-700",
    secondary: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50", 
    outline: "border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-50",
    quiet: "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
  }
  
  const sizes = {
    sm: "h-8 px-3 text-xs",
    md: "h-10 px-4 text-sm"
  }
  
  // ESLint suppressions for controlled object access
  /* eslint-disable security/detect-object-injection */
  return (
    <button
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      {...props}
    >
      {children}
    </button>
  )
}

// BIRHAUS: Simple Card component for the library
interface BirhausCardProps extends React.HTMLAttributes<HTMLDivElement> {}

function BirhausCard({ className, children, ...props }: BirhausCardProps) {
  return (
    <div
      className={cn(
        "bg-white border border-gray-200 rounded-lg shadow-sm",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

// BIRHAUS: Simple Badge component for the library
interface BirhausBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'outline'
}

function BirhausBadge({ className, variant = 'default', children, ...props }: BirhausBadgeProps) {
  const variants = {
    default: "bg-gray-100 text-gray-800",
    outline: "border border-gray-300 bg-transparent text-gray-700"
  }
  
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm px-2 py-1 text-xs font-medium",
        variants[variant],
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}

// BIRHAUS: Undo Toast Component
export interface UndoToastProps {
  action: UndoAction
  onUndo: () => void
  onDismiss: () => void
  className?: string
}

// eslint-disable-next-line max-lines-per-function -- UndoToast requires complex state management and UI logic
export function UndoToast({ action, onUndo, onDismiss, className }: UndoToastProps) {
  const [timeLeft, setTimeLeft] = useState<number>(0)
  const [isUndoing, setIsUndoing] = useState(false)
  
  useEffect(() => {
    const updateTimeLeft = () => {
      const now = new Date()
      const remaining = Math.max(0, action.expiresAt.getTime() - now.getTime())
      setTimeLeft(remaining)
      
      if (remaining === 0) {
        onDismiss()
      }
    }
    
    updateTimeLeft()
    const interval = setInterval(updateTimeLeft, 100)
    
    return () => clearInterval(interval)
  }, [action.expiresAt, onDismiss])
  
  const handleUndo = async () => {
    setIsUndoing(true)
    try {
      await onUndo()
    } catch (error) {
      console.error('Undo failed:', error)
      setIsUndoing(false)
    }
  }
  
  const progress = (timeLeft / (action.expiresAt.getTime() - action.createdAt.getTime())) * 100
  
  const priorityStyles = {
    low: 'border-blue-200 bg-blue-50',
    normal: 'border-green-200 bg-green-50',
    high: 'border-yellow-200 bg-yellow-50',
    critical: 'border-red-200 bg-red-50'
  }
  
  const priorityIcons = {
    low: <CheckCircle className="h-4 w-4 text-blue-600" />,
    normal: <CheckCircle className="h-4 w-4 text-green-600" />,
    high: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
    critical: <AlertTriangle className="h-4 w-4 text-red-600" />
  }
  
  return (
    <BirhausCard className={cn(
      "relative overflow-hidden border transition-all duration-200",
      priorityStyles[action.priority],
      className
    )}>
      {/* Progress bar */}
      <div 
        className="absolute bottom-0 left-0 h-1 bg-current opacity-30 transition-all duration-100"
        style={{ width: `${progress}%` }}
      />
      
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {action.icon || priorityIcons[action.priority]}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium">Acción completada</span>
                <BirhausBadge variant="outline" className="text-xs">
                  {action.type}
                </BirhausBadge>
              </div>
              <p className="text-sm text-gray-600">
                {action.description}
              </p>
              <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                <Clock className="h-3 w-3" />
                <span>{Math.ceil(timeLeft / 1000)}s para deshacer</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <BirhausButton
              size="sm"
              variant="outline"
              onClick={handleUndo}
              disabled={isUndoing}
              className="text-xs"
            >
              <Undo2 className="h-3 w-3 mr-1" />
              {isUndoing ? 'Deshaciendo...' : 'Deshacer'}
            </BirhausButton>
            <BirhausButton
              size="sm"
              variant="quiet"
              onClick={onDismiss}
              className="text-xs p-1"
            >
              <X className="h-3 w-3" />
            </BirhausButton>
          </div>
        </div>
      </div>
    </BirhausCard>
  )
}

// BIRHAUS: Undo Container for displaying active undo actions
export interface UndoContainerProps {
  position?: 'top-right' | 'bottom-right' | 'bottom-left' | 'top-left'
  className?: string
}

export function UndoContainer({ 
  position = 'top-right', 
  className 
}: UndoContainerProps) {
  const { actions, undoAction, dismissAction } = useUndo()
  
  const positionStyles = {
    'top-right': 'fixed top-4 right-4 z-50',
    'bottom-right': 'fixed bottom-4 right-4 z-50',
    'bottom-left': 'fixed bottom-4 left-4 z-50',
    'top-left': 'fixed top-4 left-4 z-50'
  }
  
  if (actions.length === 0) return null
  
  return (
    <div className={cn(
      positionStyles[position],
      "w-96 max-w-[calc(100vw-2rem)] space-y-3",
      className
    )}>
      {actions.map((action) => (
        <UndoToast
          key={action.id}
          action={action}
          onUndo={() => undoAction(action.id)}
          onDismiss={() => dismissAction(action.id)}
        />
      ))}
    </div>
  )
}

// BIRHAUS: Utility hook for common undo patterns
export function useUndoAction() {
  const { addAction } = useUndo()
  
  const createUndoableAction = (
    description: string,
    undoFunction: () => Promise<void>,
    options: {
      type?: UndoAction['type']
      entityType: string
      entityId: string
      priority?: UndoAction['priority']
      icon?: ReactNode
      timeoutMs?: number
    }
  ) => {
    return addAction({
      type: options.type || 'update',
      entityType: options.entityType,
      entityId: options.entityId,
      description,
      undoFunction,
      priority: options.priority || 'normal',
      icon: options.icon
    })
  }
  
  return { createUndoableAction }
}

